import React, { useEffect, useState } from "react";
import Link from "next/link";
import { CornerDownRight, Heart, Loader2, Pencil, Quote, Trash2 } from "lucide-react";
import { MentionInput, MentionText, type MentionContext } from "@/components/MentionInput";
import { FormattedBody } from "@/components/formatted-body";
import { looksLikeHtml } from "@/lib/job-posting-html";
import { ForumPhotoGrid, ForumPhotoPicker } from "@/components/forum-photos";
import { useAuth } from "@/context/auth-context";
import { AuthModal } from "@/components/auth-modal";
import { safeFormatDateTime } from "@/lib/safe-date";

interface CommentAuthor {
  id: string;
  name: string;
  username?: string;
  image?: string;
}

interface CommentItem {
  id: string;
  author: CommentAuthor;
  content: string;
  createdAt: string;
  isRemoved?: boolean;
  isDeleted?: boolean;
  isEdited?: boolean;
  likeCount?: number;
  isLiked?: boolean;
  quotedPostId?: string | null;
  quotedContent?: string | null;
  imageUrls?: string[];
  replies?: CommentItem[];
}

interface ForumCommentsProps {
  topicId: string;
  canModerate?: boolean;
  /** When set, @ mentions are scoped to study group members */
  studyGroupId?: string;
  /**
   * Comment thread endpoints. Defaults to the forum topic thread.
   * Resource pages pass their own list, edit, and reaction routes so both
   * surfaces share this behavior.
   */
  thread?: {
    list: string;
    item: (id: string) => string;
    like: (id: string) => string;
  };
  /** Called with the public comment count after a successful post or delete. */
  onCommentAdded?: (commentCount?: number) => void;
  onCommentRemoved?: (commentCount?: number) => void;
  /** Thread is closed (locked or author-deleted): existing replies stay
   *  readable but no new replies can be posted. */
  locked?: boolean;
}

function AuthorAvatar({ author }: { author: CommentAuthor }) {
  return (
    <img
      src={author.image || "/placeholder.svg"}
      alt={author.name}
      className="h-6 w-6 rounded-full object-cover flex-shrink-0 hover:ring-2 hover:ring-primary/50 transition-all"
    />
  );
}

export default function ForumComments({ topicId, canModerate = false, studyGroupId, thread, onCommentAdded, onCommentRemoved, locked = false }: ForumCommentsProps) {
  const allowPhotos = !thread;
  const endpoints = thread ?? {
    list: `/api/forums/topics/${topicId}/comments`,
    item: (id: string) => `/api/forums/posts/${id}`,
    like: (id: string) => `/api/forums/posts/${id}/like`,
  };
  const { isAuthenticated, user } = useAuth();
  const currentUserId = user?.id;
  const mentionContext: MentionContext = studyGroupId
    ? { type: "study-group", id: studyGroupId }
    : { type: "forum-topic", id: topicId };
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [input, setInput] = useState("");
  const [photos, setPhotos] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Reply state (internal)
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyInput, setReplyInput] = useState("");
  const [replyPosting, setReplyPosting] = useState(false);
  const [localReplies, setLocalReplies] = useState<Record<string, CommentItem[]>>({});
  // Like state (optimistic)
  const [postLikes, setPostLikes] = useState<Record<string, { liked: boolean; likeCount: number }>>({});
  // Quote state
  const [quotingPost, setQuotingPost] = useState<{ id: string; content: string; authorName: string } | null>(null);
  // Moderation state
  const [moderating, setModerating] = useState<Record<string, boolean>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");

  useEffect(() => {
    setLoading(true);
    fetch(endpoints.list)
      .then((res) => res.json())
      .then((data) => setComments(data.comments || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [endpoints.list]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() && !(allowPhotos && photos.length > 0)) return;
    setPostError("");
    setPosting(true);
    try {
      const res = await fetch(endpoints.list, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: input.trim(),
          ...(allowPhotos ? { imageUrls: photos } : {}),
        }),
      });
      const data = await res.json();
      if (res.ok && data.comment) {
        setComments((prev) => [...prev, { ...data.comment, replies: [] }]);
        setInput("");
        setPhotos([]);
        onCommentAdded?.(data.commentCount);
      } else {
        setPostError(data.error || "Failed to post comment. Please try again.");
      }
    } catch {
      setPostError("Failed to post comment. Please try again.");
    } finally {
      setPosting(false);
    }
  };

  const handleInlineReply = async (parentId: string) => {
    const text = replyInput.trim();
    if (!text || replyPosting) return;
    setReplyPosting(true);
    try {
      const body: Record<string, string> = { content: text, parentId };
      if (quotingPost) body.quotedPostId = quotingPost.id;
      const res = await fetch(endpoints.list, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.comment) {
        setLocalReplies((prev) => ({
          ...prev,
          [parentId]: [...(prev[parentId] || []), data.comment],
        }));
        setReplyInput("");
        setReplyingToId(null);
        setQuotingPost(null);
        onCommentAdded?.(data.commentCount);
      }
    } finally {
      setReplyPosting(false);
    }
  };

  const handleLike = async (postId: string, currentLiked: boolean, currentCount: number) => {
    setPostLikes((prev) => ({
      ...prev,
      [postId]: { liked: !currentLiked, likeCount: currentLiked ? currentCount - 1 : currentCount + 1 },
    }));
    try {
      const res = await fetch(endpoints.like(postId), { method: "POST" });
      const data = await res.json();
      if (res.ok) {
        setPostLikes((prev) => ({
          ...prev,
          [postId]: { liked: data.liked, likeCount: data.likeCount },
        }));
      } else {
        setPostLikes((prev) => ({
          ...prev,
          [postId]: { liked: currentLiked, likeCount: currentCount },
        }));
      }
    } catch {
      setPostLikes((prev) => ({
        ...prev,
        [postId]: { liked: currentLiked, likeCount: currentCount },
      }));
    }
  };

  const handleModeratePost = async (postId: string, action: "remove" | "restore") => {
    setModerating((prev) => ({ ...prev, [postId]: true }));
    try {
      const res = await fetch(endpoints.item(postId), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      if (action === "remove") onCommentRemoved?.(data.commentCount);
      if (action === "restore") onCommentAdded?.(data.commentCount);
      if (data.hardDeleted && action === "remove") {
        setComments((prev) =>
          prev
            .filter((c) => c.id !== postId)
            .map((c) => ({ ...c, replies: (c.replies || []).filter((r) => r.id !== postId) })),
        );
        setLocalReplies((prev) => {
          const next = { ...prev };
          for (const key of Object.keys(next)) {
            next[key] = next[key].filter((r) => r.id !== postId);
          }
          return next;
        });
        return;
      }
      const restored = data.comment;
      setComments((prev) =>
        prev.map((c) => {
          if (c.id === postId) {
            return {
              ...c,
              isRemoved: action === "remove",
              isDeleted: action === "remove",
              content: action === "restore" ? (restored?.content ?? c.content) : "",
              quotedContent: action === "restore" ? (restored?.quotedContent ?? c.quotedContent) : "",
            };
          }
          return {
            ...c,
            replies: (c.replies || []).map((r) =>
              r.id === postId
                ? {
                    ...r,
                    isRemoved: action === "remove",
                    isDeleted: action === "remove",
                    content: action === "restore" ? (restored?.content ?? r.content) : "",
                    quotedContent: action === "restore" ? (restored?.quotedContent ?? r.quotedContent) : "",
                  }
                : r
            ),
          };
        })
      );
      setLocalReplies((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          next[key] = next[key].map((r) =>
            r.id === postId
              ? {
                  ...r,
                  isRemoved: action === "remove",
                  isDeleted: action === "remove",
                  content: action === "restore" ? (restored?.content ?? r.content) : "",
                  quotedContent: action === "restore" ? (restored?.quotedContent ?? r.quotedContent) : "",
                }
              : r
          );
        }
        return next;
      });
    } finally {
      setModerating((prev) => ({ ...prev, [postId]: false }));
    }
  };

  const handleSaveEdit = async (postId: string) => {
    const newContent = editContent.trim();
    if (!newContent) return;
    const res = await fetch(endpoints.item(postId), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content: newContent }),
    });
    if (res.ok) {
      const patch = (row: CommentItem) =>
        row.id === postId ? { ...row, content: newContent, isEdited: true } : row;
      setComments((prev) =>
        prev.map((c) => ({
          ...patch(c),
          replies: (c.replies || []).map(patch),
        }))
      );
      setLocalReplies((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          next[key] = next[key].map(patch);
        }
        return next;
      });
    }
    setEditingId(null);
    setEditContent("");
  };

  const tombstoned = (c: CommentItem) => Boolean(c.isDeleted || c.isRemoved);

  const authorLink = (a: CommentAuthor) =>
    `/social-profile/${encodeURIComponent(a.username || a.name)}`;

  return (
    <div className="border rounded-lg p-4 bg-background">
      <h2 className="text-lg font-semibold mb-2">Comments</h2>

      {loading ? (
        <div className="mb-4 text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading comments…
        </div>
      ) : comments.length === 0 ? (
        <div className="mb-4 text-muted-foreground">No comments yet.</div>
      ) : (
        <div className="mb-4 space-y-5">
          {comments.map((comment) => {
            const allReplies = [...(comment.replies || []), ...(localReplies[comment.id] || [])];
            return (
              <div key={comment.id}>
                {/* Top-level comment */}
                <div className="flex gap-2">
                  <Link href={authorLink(comment.author)}>
                    <AuthorAvatar author={comment.author} />
                  </Link>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Link href={authorLink(comment.author)} className="text-sm font-medium hover:text-primary hover:underline">
                        {comment.author.name}
                      </Link>
                      <span className="text-xs text-muted-foreground">
                        {safeFormatDateTime(comment.createdAt)}
                        {comment.isEdited && !tombstoned(comment) && (
                          <span className="ml-1 italic">(edited)</span>
                        )}
                      </span>
                    </div>
                    {comment.quotedContent && !tombstoned(comment) && (
                      <blockquote className="mt-1 mb-1 border-l-4 border-gray-300 pl-2 py-0.5 bg-gray-50 rounded-r text-xs text-gray-500 italic line-clamp-2">
                        <span className="font-medium not-italic text-gray-400">Quote · </span>{comment.quotedContent}
                      </blockquote>
                    )}
                    <div className="text-sm mt-0.5">
                      {tombstoned(comment) ? (
                        <span className="italic text-gray-400 text-xs">[Comment deleted]
                          {(canModerate || comment.author.id === currentUserId) && (
                            <button
                              type="button"
                              onClick={() => handleModeratePost(comment.id, "restore")}
                              disabled={moderating[comment.id]}
                              className="ml-2 text-blue-500 hover:text-blue-700 not-italic"
                            >
                              {moderating[comment.id] ? "…" : "Restore"}
                            </button>
                          )}
                        </span>
                      ) : editingId === comment.id ? (
                        <div className="mt-1 space-y-1.5">
                          <textarea
                            autoFocus
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="w-full text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={2}
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              className="h-6 text-xs px-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium"
                              onClick={() => handleSaveEdit(comment.id)}
                              disabled={!editContent.trim()}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="h-6 text-xs px-2.5 text-gray-500 hover:text-gray-700"
                              onClick={() => { setEditingId(null); setEditContent(""); }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        looksLikeHtml(comment.content)
                          ? <FormattedBody html={comment.content} className="text-sm" />
                          : <MentionText text={comment.content} />
                      )}
                      {!comment.isDeleted && !comment.isRemoved && editingId !== comment.id ? (
                        <ForumPhotoGrid urls={comment.imageUrls} />
                      ) : null}
                    </div>
                    {!tombstoned(comment) && (
                      <div className="flex items-center gap-3 mt-1">
                        {(() => {
                          const ls = postLikes[comment.id];
                          const liked = ls ? ls.liked : (comment.isLiked ?? false);
                          const count = ls ? ls.likeCount : (comment.likeCount ?? 0);
                          return (
                            <button
                              type="button"
                              onClick={() => isAuthenticated ? handleLike(comment.id, liked, count) : setShowAuthModal(true)}
                              className={`text-xs font-medium flex items-center gap-1 ${liked ? "text-rose-500" : "text-muted-foreground hover:text-rose-400"}`}
                            >
                              <Heart className={`h-3 w-3 ${liked ? "fill-rose-500" : ""}`} />
                              {count > 0 && <span>{count}</span>}
                            </button>
                          );
                        })()}
                        <button
                          type="button"
                          onClick={() => {
                            if (!isAuthenticated) { setShowAuthModal(true); return; }
                            if (replyingToId === comment.id) {
                              setReplyingToId(null);
                              setReplyInput("");
                              setQuotingPost(null);
                            } else {
                              setReplyingToId(comment.id);
                              setReplyInput(`@${comment.author.username || comment.author.name} `);
                              setQuotingPost(null);
                            }
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                        >
                          <CornerDownRight className="h-3 w-3" />
                          Reply
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (!isAuthenticated) { setShowAuthModal(true); return; }
                            setReplyingToId(comment.id);
                            setReplyInput(`@${comment.author.username || comment.author.name} `);
                            setQuotingPost({ id: comment.id, content: comment.content, authorName: comment.author.name });
                          }}
                          className="text-xs text-gray-600 hover:text-gray-900 font-medium flex items-center gap-1"
                        >
                          <Quote className="h-3 w-3" />
                          Quote
                        </button>
                        {comment.author.id === currentUserId && (
                          <button
                            type="button"
                            onClick={() => { setEditingId(comment.id); setEditContent(comment.content); }}
                            className="text-xs text-gray-600 hover:text-gray-900 font-medium flex items-center gap-1 ml-auto"
                          >
                            <Pencil className="h-3 w-3" /> Edit
                          </button>
                        )}
                        {(canModerate || comment.author.id === currentUserId) && (
                          <button
                            type="button"
                            onClick={() => handleModeratePost(comment.id, "remove")}
                            disabled={moderating[comment.id]}
                            className={`text-xs text-rose-500 hover:text-rose-700 font-medium flex items-center gap-1 ${comment.author.id === currentUserId ? "" : "ml-auto"}`}
                          >
                            {moderating[comment.id] ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : comment.author.id === currentUserId && !canModerate ? (
                              <><Trash2 className="h-3 w-3" /> Delete</>
                            ) : (
                              "Remove"
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Inline reply form */}
                {replyingToId === comment.id && (
                  <div className="ml-8 mt-2">
                    {quotingPost && (
                      <div className="mb-1.5 border-l-4 border-gray-300 pl-2 py-1 bg-gray-50 rounded-r text-xs text-gray-600 flex items-start gap-1">
                        <Quote className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-2 italic">{quotingPost.content}</span>
                        <button
                          type="button"
                          onClick={() => setQuotingPost(null)}
                          className="ml-auto text-gray-400 hover:text-gray-600 flex-shrink-0"
                          aria-label="Remove quote"
                        >×</button>
                      </div>
                    )}
                    <form
                      onSubmit={(e) => { e.preventDefault(); handleInlineReply(comment.id); }}
                      className="flex gap-2"
                    >
                      <MentionInput
                        autoFocus
                        placeholder={quotingPost ? `Quoting ${quotingPost.authorName}…` : `Reply to ${comment.author.name}…`}
                        className="flex-1 border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                        value={replyInput}
                        onChange={setReplyInput}
                        disabled={replyPosting}
                        mentionContext={mentionContext}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleInlineReply(comment.id); } }}
                      />
                      <button
                        type="submit"
                        className="bg-primary text-white px-3 py-1.5 rounded text-sm disabled:opacity-50 flex items-center gap-1"
                        disabled={replyPosting || !replyInput.trim()}
                      >
                        {replyPosting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Reply"}
                      </button>
                    </form>
                  </div>
                )}

                {/* Nested replies */}
                {allReplies.length > 0 && (
                  <div className="ml-8 mt-2 space-y-3 border-l-2 border-muted pl-3">
                    {allReplies.map((reply) => (
                      <div key={reply.id} className="flex gap-2">
                        <Link href={authorLink(reply.author)}>
                          <AuthorAvatar author={reply.author} />
                        </Link>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <Link href={authorLink(reply.author)} className="text-xs font-medium hover:text-primary hover:underline">
                              {reply.author.name}
                            </Link>
                            <span className="text-[10px] text-muted-foreground">
                              {safeFormatDateTime(reply.createdAt)}
                              {reply.isEdited && !tombstoned(reply) && (
                                <span className="ml-1 italic">(edited)</span>
                              )}
                            </span>
                          </div>
                          {reply.quotedContent && !tombstoned(reply) && (
                            <blockquote className="mt-0.5 mb-0.5 border-l-4 border-gray-300 pl-2 py-0.5 bg-gray-50 rounded-r text-[10px] text-gray-500 italic line-clamp-2">
                              <span className="font-medium not-italic text-gray-400">Quote · </span>{reply.quotedContent}
                            </blockquote>
                          )}
                          <div className="text-xs mt-0.5">
                            {tombstoned(reply) ? (
                              <span className="italic text-gray-400">
                                [Comment deleted]
                                {(canModerate || reply.author.id === currentUserId) && (
                                  <button
                                    type="button"
                                    onClick={() => handleModeratePost(reply.id, "restore")}
                                    disabled={moderating[reply.id]}
                                    className="ml-2 text-blue-500 hover:text-blue-700 not-italic"
                                  >
                                    {moderating[reply.id] ? "…" : "Restore"}
                                  </button>
                                )}
                              </span>
                            ) : editingId === reply.id ? (
                              <div className="mt-0.5 space-y-1.5">
                                <textarea
                                  autoFocus
                                  value={editContent}
                                  onChange={(e) => setEditContent(e.target.value)}
                                  className="w-full text-xs border border-gray-300 rounded-lg px-2 py-1 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  rows={2}
                                />
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    className="h-5 text-[10px] px-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium"
                                    onClick={() => handleSaveEdit(reply.id)}
                                    disabled={!editContent.trim()}
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    className="h-5 text-[10px] px-2 text-gray-500 hover:text-gray-700"
                                    onClick={() => { setEditingId(null); setEditContent(""); }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              looksLikeHtml(reply.content)
                                ? <FormattedBody html={reply.content} className="text-sm" />
                                : <MentionText text={reply.content} />
                            )}
                            {!tombstoned(reply) && editingId !== reply.id ? (
                              <ForumPhotoGrid urls={reply.imageUrls} />
                            ) : null}
                          </div>
                          {!tombstoned(reply) && (
                            <div className="flex items-center gap-3 mt-0.5">
                              {(() => {
                                const ls = postLikes[reply.id];
                                const liked = ls ? ls.liked : (reply.isLiked ?? false);
                                const count = ls ? ls.likeCount : (reply.likeCount ?? 0);
                                return (
                                  <button
                                    type="button"
                                    onClick={() => isAuthenticated ? handleLike(reply.id, liked, count) : setShowAuthModal(true)}
                                    className={`text-[10px] font-medium flex items-center gap-1 ${liked ? "text-rose-500" : "text-muted-foreground hover:text-rose-400"}`}
                                  >
                                    <Heart className={`h-2.5 w-2.5 ${liked ? "fill-rose-500" : ""}`} />
                                    {count > 0 && <span>{count}</span>}
                                  </button>
                                );
                              })()}
                              <button
                                type="button"
                                onClick={() => {
                                  if (!isAuthenticated) { setShowAuthModal(true); return; }
                                  setReplyingToId(comment.id);
                                  setReplyInput(`@${reply.author.username || reply.author.name} `);
                                  setQuotingPost(null);
                                }}
                                className="text-[10px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                              >
                                <CornerDownRight className="h-2.5 w-2.5" />
                                Reply
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!isAuthenticated) { setShowAuthModal(true); return; }
                                  setReplyingToId(comment.id);
                                  setReplyInput(`@${reply.author.username || reply.author.name} `);
                                  setQuotingPost({ id: reply.id, content: reply.content, authorName: reply.author.name });
                                }}
                                className="text-[10px] text-gray-600 hover:text-gray-900 font-medium flex items-center gap-1"
                              >
                                <Quote className="h-2.5 w-2.5" />
                                Quote
                              </button>
                              {reply.author.id === currentUserId && (
                                <button
                                  type="button"
                                  onClick={() => { setEditingId(reply.id); setEditContent(reply.content); }}
                                  className="text-[10px] text-gray-600 hover:text-gray-900 font-medium ml-auto flex items-center gap-0.5"
                                >
                                  <Pencil className="h-2.5 w-2.5" /> Edit
                                </button>
                              )}
                              {(canModerate || reply.author.id === currentUserId) && (
                                <button
                                  type="button"
                                  onClick={() => handleModeratePost(reply.id, "remove")}
                                  disabled={moderating[reply.id]}
                                  className={`text-[10px] text-rose-500 hover:text-rose-700 font-medium flex items-center gap-0.5 ${reply.author.id === currentUserId ? "" : "ml-auto"}`}
                                >
                                  {moderating[reply.id] ? (
                                    "…"
                                  ) : reply.author.id === currentUserId && !canModerate ? (
                                    <><Trash2 className="h-2.5 w-2.5" /> Delete</>
                                  ) : (
                                    "Remove"
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {postError && <p className="text-sm text-red-500 mb-2">{postError}</p>}

      {locked ? (
        <p className="w-full border border-dashed rounded px-4 py-3 text-sm text-muted-foreground text-center">
          This discussion is closed — no new replies can be added.
        </p>
      ) : isAuthenticated ? (
        <form className="space-y-2" onSubmit={handleSubmit}>
          {allowPhotos ? <ForumPhotoPicker urls={photos} onChange={setPhotos} disabled={posting} /> : null}
          <div className="flex gap-2">
          <MentionInput
            placeholder="Add a comment…"
            className="flex-1 border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            value={input}
            onChange={setInput}
            disabled={posting}
            mentionContext={mentionContext}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(e as any); } }}
          />
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-50 flex items-center gap-1"
            disabled={posting || (!input.trim() && photos.length === 0)}
          >
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post"}
          </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setShowAuthModal(true)}
          className="w-full border rounded px-4 py-3 text-sm text-muted-foreground text-left hover:bg-muted/40 transition-colors"
        >
          Login to join the conversation…
        </button>
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode="login"
        title="Login to participate"
        description="Create a free account to comment, reply, and like posts in the forums."
      />
    </div>
  );
}
